/**
 * Tool transform logic for the Now Assist Skill Plugin.
 * Converts XML records back to Fluent tool definitions (reverse direction).
 * Used for XML -> Fluent conversion.
 */

import {
    TOOL_TYPES,
    TOOL_RESOURCE_CAPABILITIES,
    TOOL_ATTRIBUTE_IDS,
    UNSUPPORTED_TOOL_CAPABILITY_IDS,
    SYS_ONE_EXTEND_CAPABILITY_DEFINITION_API_TYPES,
    SCRIPT_TOOL_API_ID,
    WEBSEARCH_AI_ANSWERS_CONFIG,
    WEBSEARCH_SCRAPING_CONFIG,
    AI_SEARCH_PROVIDERS,
    SKILL_TOOL_CONFIG,
} from './defaults'
import { Record as BuildRecord, type Shape, CallExpressionShape, DeletedShape } from '@servicenow/sdk-build-core'
import type { ToolDefinition } from './tool-types'
import {
    extractDependsFromEdges,
    extractConditionFromMapping,
    getAttributeMappingsForTool,
    resolveAttributeName,
} from './tool-utils'

/**
 * Convert XML records back to Fluent tool definitions
 * Used for XML -> Fluent conversion
 *
 * Detection logic classifies each resource mapping into one of:
 * - Supported tool -> converted to ToolDefinition (currently only inline-script)
 * - Unsupported tool -> records returned for Record API fallback (PI, DocIntel, Retriever)
 * - Unsupported metric -> metric records returned for Record API fallback
 *
 * Classification order:
 * 1. Skip self-mapping (resource_capability === parentCapabilityId)
 * 2. Known unsupported tools by fixed capability IDs (UNSUPPORTED_TOOL_CAPABILITY_IDS)
 * 3. Known supported tools by fixed capability IDs (inline-script, decision, web-search)
 * 4. DATASOURCE tools identified via capability definition api/api_type (script, subflow, flow-action)
 * 5. Unknown fallback: edge heuristic (has edges -> tool, no edges -> metric)
 *
 * @param resourceMappings - All resource mapping records for the skill
 * @param attributeMappings - All attribute mapping records for the skill
 * @param edges - All edge records for the skill
 * @param capabilities - All capability records (sys_one_extend_capability) for type lookup
 * @param capabilityDefinitions - All capability definition records for api_type/api lookup
 * @param parentCapabilityId - The capability ID of the skill itself (to skip its own mapping)
 * @param definitionAttributes - Optional definition attribute records for resolving attribute names
 * @returns Object containing tools array and arrays of unsupported tool/metric records
 */
export function generateTools(
    resourceMappings: BuildRecord[],
    attributeMappings: BuildRecord[],
    edges: BuildRecord[],
    capabilities: BuildRecord[],
    capabilityDefinitions: BuildRecord[],
    parentCapabilityId: string,
    definitionAttributes?: BuildRecord[]
): {
    tools: ToolDefinition[]
    unsupportedTools: BuildRecord[]
    unsupportedMetrics: BuildRecord[]
    supportedToolsToDelete: BuildRecord[]
} {
    const unsupportedTools: BuildRecord[] = []
    const unsupportedMetrics: BuildRecord[] = []

    // Lookup: definition attribute sys_id → { name, dataType }
    // Used to resolve resource_attribute_name (which stores sys_id) back to human-readable name and type
    const defAttrInfoMap = new Map<string, { name: string; dataType: string }>()
    if (definitionAttributes) {
        for (const attr of definitionAttributes) {
            const attrId = attr.getId().getValue() as string
            const attrName = attr.get('name')?.getValue() as string
            const dataTypeValue = attr.get('data_type')?.getValue()
            const dataType = typeof dataTypeValue === 'string' ? dataTypeValue : 'string'
            if (attrId && attrName) {
                defAttrInfoMap.set(attrId, { name: attrName, dataType })
            }
        }
    }
    // Legacy map for backward compatibility with resolveAttributeName helper
    const defAttrNameMap = new Map<string, string>()
    for (const [id, info] of defAttrInfoMap) {
        defAttrNameMap.set(id, info.name)
    }

    // Lookup: capabilityId -> capability record (for type check: SKILL, METRIC, etc.)
    const capabilityMap = new Map<string, BuildRecord>()
    for (const cap of capabilities) {
        capabilityMap.set(cap.getId().getValue() as string, cap)
    }

    // Lookup: capabilityId -> capability definition record
    const capDefMap = new Map<string, BuildRecord>()
    for (const def of capabilityDefinitions) {
        const capId = def.get('capability')?.getValue()
        if (typeof capId === 'string') {
            capDefMap.set(capId, def)
        }
    }

    // Set of resource mapping IDs that participate in any edge (tools always have edges, metrics don't)
    const mappingIdsWithEdges = new Set<string>()
    for (const edge of edges) {
        const fromId = edge.get('from_resource')?.getValue()
        const toId = edge.get('to_resource')?.getValue()
        if (typeof fromId === 'string') {
            mappingIdsWithEdges.add(fromId)
        }
        if (typeof toId === 'string') {
            mappingIdsWithEdges.add(toId)
        }
    }

    // -- Pass 1: Detection -- classify each resource mapping --
    // Supported tool mappings are collected for processing in pass 2
    const supportedToolMappings: { mapping: BuildRecord; type: string }[] = []
    // Track parent skill mapping IDs (self-mappings) to exclude from Decision target edges
    const parentMappingIds = new Set<string>()

    for (const mapping of resourceMappings) {
        const resourceCapId = mapping.get('resource_capability')?.getValue() as string
        const mappingId = mapping.getId().getValue() as string

        // 1. Skip self-mapping (skill maps to itself as root of tool graph)
        if (resourceCapId === parentCapabilityId) {
            parentMappingIds.add(mappingId)
            continue
        }

        // 1b. Skip mappings that don't belong to this skill (parent_capability mismatch)
        // This handles the case where multiple skills in the same scope have their
        // resource mappings included in descendants - we only want mappings for THIS skill
        const mappingParentCapId = mapping.get('parent_capability')?.toString().getValue() as string
        if (mappingParentCapId && mappingParentCapId !== parentCapabilityId) {
            continue
        }

        // 2. Known unsupported tools (DocIntel, PI, Retriever, Websearch maint variants)
        if (UNSUPPORTED_TOOL_CAPABILITY_IDS.has(resourceCapId)) {
            unsupportedTools.push(mapping)
            continue
        }

        // 3. Known supported tools by fixed capability IDs
        if (resourceCapId === TOOL_RESOURCE_CAPABILITIES.INLINE_SCRIPT) {
            supportedToolMappings.push({ mapping, type: TOOL_TYPES.INLINE_SCRIPT })
            continue
        }

        if (resourceCapId === TOOL_RESOURCE_CAPABILITIES.DECISION) {
            supportedToolMappings.push({ mapping, type: TOOL_TYPES.DECISION })
            continue
        }

        if (resourceCapId === TOOL_RESOURCE_CAPABILITIES.WEB_SEARCH) {
            supportedToolMappings.push({ mapping, type: TOOL_TYPES.WEB_SEARCH })
            continue
        }

        // 4. Capability in descendants -- check type from capability record, api/api_type from definition
        const cap = capabilityMap.get(resourceCapId)
        const capDef = capDefMap.get(resourceCapId)

        if (cap || capDef) {
            const capType = (cap?.get('type')?.getValue() as string)?.toUpperCase()

            if (capType === 'SKILL') {
                supportedToolMappings.push({ mapping, type: TOOL_TYPES.SKILL })
                continue
            }

            const apiType = capDef?.get('api_type')?.getValue() as string
            const api = capDef?.get('api')?.getValue() as string

            if (api === SCRIPT_TOOL_API_ID) {
                supportedToolMappings.push({ mapping, type: TOOL_TYPES.SCRIPT })
                continue
            }

            if (apiType === SYS_ONE_EXTEND_CAPABILITY_DEFINITION_API_TYPES.SYS_HUB_FLOW) {
                supportedToolMappings.push({ mapping, type: TOOL_TYPES.SUBFLOW })
                continue
            }

            if (apiType === SYS_ONE_EXTEND_CAPABILITY_DEFINITION_API_TYPES.SYS_HUB_ACTION_TYPE_DEFINITION) {
                supportedToolMappings.push({ mapping, type: TOOL_TYPES.FLOW_ACTION })
                continue
            }
        }

        // 4b. Check Skill tool by fixed capability ID
        if (resourceCapId === TOOL_RESOURCE_CAPABILITIES.SKILL) {
            supportedToolMappings.push({ mapping, type: TOOL_TYPES.SKILL })
            continue
        }

        // 4c. Fallback: check metadata.toolType for tools built by Fluent SDK
        // This handles the case where the build stored the tool type in metadata
        // without creating separate capability/capDef records
        const metadataStr = mapping.get('metadata')?.getValue() as string
        if (metadataStr) {
            try {
                const metadata = JSON.parse(metadataStr)
                const toolType = metadata.toolType as string
                const knownToolTypes: string[] = [
                    TOOL_TYPES.SCRIPT,
                    TOOL_TYPES.SUBFLOW,
                    TOOL_TYPES.FLOW_ACTION,
                    TOOL_TYPES.SKILL,
                    TOOL_TYPES.DECISION,
                ]
                if (toolType && knownToolTypes.includes(toolType)) {
                    supportedToolMappings.push({ mapping, type: toolType })
                    continue
                }
            } catch {}
        }

        // 5. Unknown tools or metrics
        if (mappingIdsWithEdges.has(mappingId)) {
            unsupportedTools.push(mapping)
        } else {
            unsupportedMetrics.push(mapping)
        }
    }

    // If any unsupported tools exist, don't process supported ones -- caller will return partial
    if (unsupportedTools.length > 0) {
        return { tools: [], unsupportedTools, unsupportedMetrics, supportedToolsToDelete: [] }
    }

    // -- Pass 2: Processing -- build ToolDefinitions for supported tool mappings --
    const tools: ToolDefinition[] = []

    // Lookup: resource mapping ID -> resource_name (for dependency resolution)
    const mappingIdToName = new Map<string, string>()
    for (const mapping of resourceMappings) {
        const id = mapping.getId().getValue() as string
        const name = mapping.get('resource_name')?.getValue() as string
        if (id && name) {
            mappingIdToName.set(id, name)
        }
    }

    // Collect Decision mapping IDs so we can exclude Decision routing edges from depends
    const decisionMappingIds = new Set<string>()
    for (const { mapping, type } of supportedToolMappings) {
        if (type === TOOL_TYPES.DECISION) {
            decisionMappingIds.add(mapping.getId().getValue() as string)
        }
    }

    for (const { mapping, type } of supportedToolMappings) {
        switch (type) {
            case TOOL_TYPES.INLINE_SCRIPT: {
                const tool = buildInlineScriptFromRecord(
                    mapping,
                    attributeMappings,
                    edges,
                    mappingIdToName,
                    decisionMappingIds
                )
                if (tool) {
                    tools.push(tool)
                }
                break
            }
            case TOOL_TYPES.WEB_SEARCH: {
                const tool = buildWebSearchFromRecord(
                    mapping,
                    attributeMappings,
                    edges,
                    mappingIdToName,
                    decisionMappingIds
                )
                if (tool) {
                    tools.push(tool)
                }
                break
            }
            case TOOL_TYPES.SCRIPT: {
                const tool = buildScriptFromRecord(
                    mapping,
                    attributeMappings,
                    edges,
                    mappingIdToName,
                    capDefMap,
                    decisionMappingIds,
                    defAttrNameMap,
                    capabilityMap
                )
                if (tool) {
                    tools.push(tool)
                }
                break
            }
            case TOOL_TYPES.SUBFLOW: {
                const tool = buildSubflowFromRecord(
                    mapping,
                    attributeMappings,
                    edges,
                    mappingIdToName,
                    capDefMap,
                    decisionMappingIds,
                    defAttrInfoMap,
                    capabilityMap
                )
                if (tool) {
                    tools.push(tool)
                }
                break
            }
            case TOOL_TYPES.FLOW_ACTION: {
                const tool = buildFlowActionFromRecord(
                    mapping,
                    attributeMappings,
                    edges,
                    mappingIdToName,
                    decisionMappingIds,
                    capDefMap,
                    defAttrInfoMap,
                    capabilityMap
                )
                if (tool) {
                    tools.push(tool)
                }
                break
            }
            case TOOL_TYPES.SKILL: {
                const tool = buildSkillFromRecord(
                    mapping,
                    attributeMappings,
                    edges,
                    mappingIdToName,
                    decisionMappingIds,
                    defAttrNameMap
                )
                if (tool) {
                    tools.push(tool)
                }
                break
            }
            case TOOL_TYPES.DECISION: {
                const tool = buildDecisionFromRecord(
                    mapping,
                    attributeMappings,
                    edges,
                    mappingIdToName,
                    parentMappingIds,
                    decisionMappingIds
                )
                if (tool) {
                    tools.push(tool)
                }
                break
            }
        }
    }

    // Post-processing: Add Decision dependencies to their targets
    // Decision targets should depend on the Decision for proper execution order
    const toolByName = new Map<string, ToolDefinition>()
    for (const tool of tools) {
        toolByName.set(tool.name, tool)
    }

    for (const tool of tools) {
        if (tool.type === TOOL_TYPES.DECISION && tool.targets) {
            for (const targetName of tool.targets) {
                // Skip '_end' - it's not a real tool
                if (targetName === '_end') {
                    continue
                }
                const targetTool = toolByName.get(targetName)
                if (targetTool) {
                    // Add Decision to target's depends if not already present
                    if (!targetTool.depends) {
                        targetTool.depends = [tool.name]
                    } else if (!targetTool.depends.includes(tool.name)) {
                        targetTool.depends.push(tool.name)
                    }
                }
            }
        }
    }

    // ── Collect DELETE records for supported tool Record API files ──
    // When all tools are supported, any tool records that came from Record API (Record() calls)
    // should be deleted during transform so they don't conflict with the Fluent API tools.
    // We must delete ALL three table types: resource_mapping, attribute_mapping, and edge,
    // plus the parent/self-mapping record (skill maps to itself as root of tool graph).
    const supportedToolsToDelete: BuildRecord[] = []
    if (unsupportedTools.length === 0) {
        // Delete parent/self-mapping Record API files (resource_capability === parentCapabilityId)
        for (const mapping of resourceMappings) {
            const resourceCapId = mapping.get('resource_capability')?.getValue() as string
            if (resourceCapId === parentCapabilityId) {
                const source = mapping.asRecord().getSource()
                if (source instanceof CallExpressionShape && source.getCallee() === 'Record') {
                    supportedToolsToDelete.push(
                        new BuildRecord({
                            id: mapping.asRecord().getId(),
                            properties: {},
                            action: 'DELETE',
                            source: new DeletedShape({ source }),
                        })
                    )
                }
            }
        }

        // Delete supported tool mapping Record API files
        for (const { mapping } of supportedToolMappings) {
            const source = mapping.asRecord().getSource()
            if (source instanceof CallExpressionShape && source.getCallee() === 'Record') {
                supportedToolsToDelete.push(
                    new BuildRecord({
                        id: mapping.asRecord().getId(),
                        properties: {},
                        action: 'DELETE',
                        source: new DeletedShape({ source }),
                    })
                )
            }
        }

        // Delete ALL Record API attribute mapping files when transitioning to Fluent.
        for (const attr of attributeMappings) {
            const attrSource = attr.asRecord().getSource()
            if (attrSource instanceof CallExpressionShape && attrSource.getCallee() === 'Record') {
                supportedToolsToDelete.push(
                    new BuildRecord({
                        id: attr.asRecord().getId(),
                        properties: {},
                        action: 'DELETE',
                        source: new DeletedShape({ source: attrSource }),
                    })
                )
            }
        }

        // Delete ALL Record API edge files when transitioning to Fluent.
        for (const edge of edges) {
            const edgeSource = edge.asRecord().getSource()
            if (edgeSource instanceof CallExpressionShape && edgeSource.getCallee() === 'Record') {
                supportedToolsToDelete.push(
                    new BuildRecord({
                        id: edge.asRecord().getId(),
                        properties: {},
                        action: 'DELETE',
                        source: new DeletedShape({ source: edgeSource }),
                    })
                )
            }
        }

        // Delete ALL Record API definition attribute files for tool-level def attrs.
        if (definitionAttributes) {
            for (const defAttr of definitionAttributes) {
                const defAttrCapId = defAttr.get('capability')?.getValue() as string
                if (defAttrCapId !== parentCapabilityId) {
                    const defAttrSource = defAttr.asRecord().getSource()
                    if (defAttrSource instanceof CallExpressionShape && defAttrSource.getCallee() === 'Record') {
                        supportedToolsToDelete.push(
                            new BuildRecord({
                                id: defAttr.asRecord().getId(),
                                properties: {},
                                action: 'DELETE',
                                source: new DeletedShape({ source: defAttrSource }),
                            })
                        )
                    }
                }
            }
        }
    }

    return { tools, unsupportedTools, unsupportedMetrics, supportedToolsToDelete }
}

// -- Transform helper functions: XML records -> ToolDefinition --

/**
 * Build an InlineScript ToolDefinition from XML records.
 */
function buildInlineScriptFromRecord(
    mapping: BuildRecord,
    attributeMappings: BuildRecord[],
    edges: BuildRecord[],
    mappingIdToName: Map<string, string>,
    decisionMappingIds: Set<string>
): ToolDefinition | null {
    const mappingId = mapping.getId().getValue() as string
    const name = mapping.get('resource_name')?.getValue() as string
    if (!name) {
        return null
    }

    const { inputs, outputs } = getAttributeMappingsForTool(mappingId, attributeMappings)

    // Extract script from input attribute (attribute_value_template or attribute_value_script)
    let script: string | undefined
    for (const input of inputs) {
        const attrName = input.get('resource_attribute_name')?.getValue() as string
        if (attrName === TOOL_ATTRIBUTE_IDS.INLINE_SCRIPT_INPUT) {
            const template = input.get('attribute_value_template')?.getValue() as string
            const scriptVal = input.get('attribute_value_script')?.getValue() as string
            script = template || scriptVal || ''
            // Keep the full script including the (function runScript(context){...})(context); wrapper.
            // This gives users full visibility of what's in the XML.
            // The build path already handles this -- it checks startsWith('(function')
            // and won't double-wrap.
        }
    }

    // Extract truncate from output attribute
    let truncate = false
    for (const output of outputs) {
        const truncateVal = output.get('truncate')?.getValue()
        if (truncateVal === true || truncateVal === 'true') {
            truncate = true
        }
    }

    const depends = extractDependsFromEdges(mappingId, edges, mappingIdToName, decisionMappingIds)
    const condition = extractConditionFromMapping(mapping)

    return {
        type: TOOL_TYPES.INLINE_SCRIPT,
        name,
        $id: mappingId,
        script,
        truncate,
        depends,
        condition,
    }
}

/**
 * Build a WebSearch ToolDefinition from XML records.
 * Reverses the build process: attribute IDs -> fluent property names.
 */
function buildWebSearchFromRecord(
    mapping: BuildRecord,
    attributeMappings: BuildRecord[],
    edges: BuildRecord[],
    mappingIdToName: Map<string, string>,
    decisionMappingIds: Set<string>
): ToolDefinition | null {
    const mappingId = mapping.getId().getValue() as string
    const name = mapping.get('resource_name')?.getValue() as string
    if (!name) {
        return null
    }

    const { inputs, outputs } = getAttributeMappingsForTool(mappingId, attributeMappings)

    // Build reverse lookup: attributeId -> input attribute record
    const inputByAttrId = new Map<string, BuildRecord>()
    for (const input of inputs) {
        const attrId = input.get('resource_attribute_name')?.getValue() as string
        if (attrId) {
            inputByAttrId.set(attrId, input)
        }
    }

    // Determine searchType first -- needed to pick the right config
    const searchTypeAttrId = WEBSEARCH_AI_ANSWERS_CONFIG.INPUT_MAPPINGS.find(
        (m) => m.fluentName === 'searchType'
    )?.attributeId
    let searchType: 'ai_answers' | 'searching_and_scraping' | undefined
    if (searchTypeAttrId) {
        const searchTypeRecord = inputByAttrId.get(searchTypeAttrId)
        const val = searchTypeRecord?.get('attribute_value_template')?.getValue() as string
        if (val === 'ai_answers' || val === 'searching_and_scraping') {
            searchType = val
        }
    }

    // Pick the input mapping config based on variant
    const inputMappingConfig =
        searchType === 'searching_and_scraping'
            ? WEBSEARCH_SCRAPING_CONFIG.INPUT_MAPPINGS
            : WEBSEARCH_AI_ANSWERS_CONFIG.INPUT_MAPPINGS

    // Extract values by reverse-mapping attributeIds
    // Also track scripted fields (attribute_value_type='script')
    const toolValues: Record<string, string | undefined> = {}
    const scriptedFieldValues: Record<string, { scriptValue: string; valueType: 'script' }> = {}
    for (const attrConfig of inputMappingConfig) {
        const record = inputByAttrId.get(attrConfig.attributeId)
        if (record) {
            const valueType = (record.get('attribute_value_type')?.getValue() as string) || 'template'
            if (valueType === 'script' && (attrConfig.fluentName === 'query' || attrConfig.fluentName === 'sites')) {
                const scriptVal = record.get('attribute_value_script')?.getValue() as string
                if (scriptVal) {
                    scriptedFieldValues[attrConfig.fluentName] = { scriptValue: scriptVal, valueType: 'script' }
                }
            } else {
                const val = record.get('attribute_value_template')?.getValue() as string
                if (val !== undefined && val !== '') {
                    toolValues[attrConfig.fluentName] = val
                }
            }
        }
    }

    // Extract query -- may be a template expression, static string, or scripted value
    let query: string
    let queryScriptValue: string | undefined
    let queryValueType: 'template' | 'script' | undefined
    if (scriptedFieldValues['query']) {
        query = ''
        queryScriptValue = scriptedFieldValues['query'].scriptValue
        queryValueType = 'script'
    } else {
        query = toolValues['query'] || ''
    }

    // Extract sites -- may be a template expression, static string, or scripted value
    let sites: string | undefined
    let sitesScriptValue: string | undefined
    let sitesValueType: 'template' | 'script' | undefined
    if (scriptedFieldValues['sites']) {
        sites = ''
        sitesScriptValue = scriptedFieldValues['sites'].scriptValue
        sitesValueType = 'script'
    } else {
        sites = toolValues['sites'] || undefined
    }

    // Extract aiSearchProviders -- reverse JSON to simple string
    let aiSearchProviders: string | undefined
    const aiProvidersJson = toolValues['aiSearchProviders']
    if (aiProvidersJson) {
        try {
            const parsed = JSON.parse(aiProvidersJson)
            const providerValue = parsed.value as string
            // Verify it's a known provider
            if (providerValue && providerValue in AI_SEARCH_PROVIDERS) {
                aiSearchProviders = providerValue
            } else {
                aiSearchProviders = providerValue || aiProvidersJson
            }
        } catch {
            // Not JSON, use as-is
            aiSearchProviders = aiProvidersJson
        }
    }

    // Extract numeric values
    const numResultsStr = toolValues['numResults']
    const numResults = numResultsStr ? parseInt(numResultsStr, 10) : undefined

    const country = toolValues['country'] || undefined

    const maxTokensStr = toolValues['maxTokens']
    const maxTokens = maxTokensStr ? parseInt(maxTokensStr, 10) : undefined

    // Extract per-output truncate config from output attribute records
    const outputAttrIdToName: Record<string, string> = {}
    for (const outMapping of WEBSEARCH_AI_ANSWERS_CONFIG.OUTPUT_MAPPINGS) {
        outputAttrIdToName[outMapping.attributeId] = outMapping.name
    }

    // Map output names to FixedOutputTruncateConfig keys (same as build direction)
    const outputNameToTruncateKey: Record<string, string> = {
        provider: 'provider',
        response: 'response',
        error: 'error',
        errorcode: 'errorcode',
        status: 'status',
    }

    let truncateConfig: Record<string, boolean> | undefined
    for (const output of outputs) {
        const attrId = output.get('resource_attribute_name')?.getValue() as string
        const outputName = outputAttrIdToName[attrId]
        if (outputName) {
            const truncateVal = output.get('truncate')?.getValue()
            const isTruncated = truncateVal === true || truncateVal === 'true'
            if (isTruncated) {
                if (!truncateConfig) {
                    truncateConfig = {}
                }
                const configKey = outputNameToTruncateKey[outputName] || outputName
                truncateConfig[configKey] = true
            }
        }
    }

    const depends = extractDependsFromEdges(mappingId, edges, mappingIdToName, decisionMappingIds)
    const condition = extractConditionFromMapping(mapping)

    return {
        type: TOOL_TYPES.WEB_SEARCH,
        name,
        $id: mappingId,
        searchType,
        query,
        queryScriptValue,
        queryValueType,
        sites,
        sitesScriptValue,
        sitesValueType,
        aiSearchProviders,
        numResults: numResults && !isNaN(numResults) ? numResults : undefined,
        country,
        maxTokens: maxTokens && !isNaN(maxTokens) ? maxTokens : undefined,
        truncate: false,
        truncateConfig,
        depends,
        condition,
    }
}

/**
 * Build a Script ToolDefinition from XML records.
 */
function buildScriptFromRecord(
    mapping: BuildRecord,
    attributeMappings: BuildRecord[],
    edges: BuildRecord[],
    mappingIdToName: Map<string, string>,
    capDefMap: Map<string, BuildRecord>,
    decisionMappingIds: Set<string>,
    defAttrNameMap?: Map<string, string>,
    capabilityMap?: Map<string, BuildRecord>
): ToolDefinition | null {
    const mappingId = mapping.getId().getValue() as string
    const name = mapping.get('resource_name')?.getValue() as string
    if (!name) {
        return null
    }

    const { inputs: inputRecords, outputs: outputRecords } = getAttributeMappingsForTool(mappingId, attributeMappings)

    // Extract scriptId and scriptFunctionName from metadata
    let scriptId: string | undefined
    let scriptFunctionName: string | undefined
    const metadataStr = mapping.get('metadata')?.getValue() as string
    if (metadataStr) {
        try {
            const metadata = JSON.parse(metadataStr)
            scriptId = metadata.scriptId
            scriptFunctionName = metadata.scriptFunctionName
        } catch {}
    }

    // If scriptId not in resource mapping metadata, try capability definition metadata
    // (instance-sourced XML stores scriptId/scriptFunctionName in the capability definition metadata)
    if (!scriptId) {
        const resourceCapId = mapping.get('resource_capability')?.getValue() as string
        const capDef = capDefMap.get(resourceCapId)
        if (capDef) {
            const capDefMetadataStr = capDef.get('metadata')?.getValue() as string
            if (capDefMetadataStr) {
                try {
                    const capDefMetadata = JSON.parse(capDefMetadataStr)
                    if (capDefMetadata.scriptId) {
                        scriptId = capDefMetadata.scriptId
                    }
                    if (capDefMetadata.scriptFunctionName && !scriptFunctionName) {
                        scriptFunctionName = capDefMetadata.scriptFunctionName
                    }
                } catch {}
            }
            // Final fallback: use capability definition api field
            // (only if api is NOT the SCRIPT_TOOL_API_ID which is a detection marker, not a script include)
            if (!scriptId) {
                const api = capDef.get('api')?.getValue() as string
                if (api && api !== SCRIPT_TOOL_API_ID) {
                    scriptId = api
                }
            }
        }
    }

    // Extract inputs
    const inputs: Array<{
        $id?: string
        name: string
        value: string
        scriptValue?: string
        valueType?: 'template' | 'script'
    }> = []
    for (const inputRecord of inputRecords) {
        const rawAttrName = inputRecord.get('resource_attribute_name')?.getValue() as string
        const attrName = resolveAttributeName(rawAttrName, defAttrNameMap)
        const valueType = (inputRecord.get('attribute_value_type')?.getValue() as string) || 'template'
        if (attrName) {
            if (valueType === 'script') {
                const scriptValue = inputRecord.get('attribute_value_script')?.getValue() as string
                inputs.push({
                    $id: rawAttrName,
                    name: attrName,
                    value: '',
                    scriptValue: scriptValue || '',
                    valueType: 'script',
                })
            } else {
                const template = inputRecord.get('attribute_value_template')?.getValue() as string
                inputs.push({ $id: rawAttrName, name: attrName, value: template || '' })
            }
        }
    }

    // Extract truncate and output from output records
    let truncate = false
    let output: { $id: string; name?: string; type?: string } | undefined
    for (const outputRec of outputRecords) {
        const truncateVal = outputRec.get('truncate')?.getValue()
        if (truncateVal === true || truncateVal === 'true') {
            truncate = true
        }
        const rawAttrName = outputRec.get('resource_attribute_name')?.getValue() as string
        if (rawAttrName) {
            // Resolve output name and type from the definition attribute
            const outputName = resolveAttributeName(rawAttrName, defAttrNameMap)
            // Build output object — only include name/type if they differ from defaults
            output = {
                $id: rawAttrName,
                ...(outputName && outputName !== 'output' ? { name: outputName } : {}),
            }
        }
    }

    const depends = extractDependsFromEdges(mappingId, edges, mappingIdToName, decisionMappingIds)
    const condition = extractConditionFromMapping(mapping)

    // Extract $capabilityId from the tool's capability record
    // Prefer explicit NowIdKey if available, otherwise fall back to the sys_id
    const resourceCapId = mapping.get('resource_capability')?.getValue() as string
    const capRecord = capabilityMap?.get(resourceCapId)
    const $capabilityId = capRecord
        ? ((capRecord.getId().getNowIdKey() as string | undefined) ?? resourceCapId)
        : resourceCapId

    return {
        type: TOOL_TYPES.SCRIPT,
        name,
        $id: mappingId,
        $capabilityId,
        output,
        scriptId,
        scriptFunctionName,
        inputs: inputs.length > 0 ? inputs : undefined,
        truncate,
        depends,
        condition,
    }
}

/**
 * Build a Subflow ToolDefinition from XML records.
 */
function buildSubflowFromRecord(
    mapping: BuildRecord,
    attributeMappings: BuildRecord[],
    edges: BuildRecord[],
    mappingIdToName: Map<string, string>,
    capDefMap: Map<string, BuildRecord>,
    decisionMappingIds: Set<string>,
    defAttrInfoMap?: Map<string, { name: string; dataType: string }>,
    capabilityMap?: Map<string, BuildRecord>
): ToolDefinition | null {
    const mappingId = mapping.getId().getValue() as string
    const name = mapping.get('resource_name')?.getValue() as string
    if (!name) {
        return null
    }

    const { inputs: inputRecords, outputs: outputRecords } = getAttributeMappingsForTool(mappingId, attributeMappings)

    // Extract subflowId from metadata
    let subflowId: string | undefined
    const metadataStr = mapping.get('metadata')?.getValue() as string
    if (metadataStr) {
        try {
            const metadata = JSON.parse(metadataStr)
            subflowId = metadata.subflowId
        } catch {}
    }

    // If subflowId not in metadata, try capability definition api field
    if (!subflowId) {
        const resourceCapId = mapping.get('resource_capability')?.getValue() as string
        const capDef = capDefMap.get(resourceCapId)
        if (capDef) {
            const api = capDef.get('api')?.getValue() as string
            if (api) {
                subflowId = api
            }
        }
    }

    // Helper to resolve attribute name and type from defAttrInfoMap
    const resolveAttrInfo = (rawAttrName: string): { name: string; dataType: string } | null => {
        if (!rawAttrName) {
            return null
        }
        const info = defAttrInfoMap?.get(rawAttrName)
        if (info) {
            return info
        }
        // Fallback: use raw name as-is with default type
        return { name: rawAttrName, dataType: 'string' }
    }

    // Extract inputs (with type from definition attribute)
    const inputs: Array<{
        $id?: string
        name: string
        type?: string
        value: string
        scriptValue?: string
        valueType?: 'template' | 'script'
    }> = []
    for (const inputRecord of inputRecords) {
        const rawAttrName = inputRecord.get('resource_attribute_name')?.getValue() as string
        const attrInfo = resolveAttrInfo(rawAttrName)
        const valueType = (inputRecord.get('attribute_value_type')?.getValue() as string) || 'template'
        if (attrInfo) {
            if (valueType === 'script') {
                const scriptValue = inputRecord.get('attribute_value_script')?.getValue() as string
                inputs.push({
                    $id: rawAttrName,
                    name: attrInfo.name,
                    type: attrInfo.dataType,
                    value: '',
                    scriptValue: scriptValue || '',
                    valueType: 'script',
                })
            } else {
                const template = inputRecord.get('attribute_value_template')?.getValue() as string
                inputs.push({ $id: rawAttrName, name: attrInfo.name, type: attrInfo.dataType, value: template || '' })
            }
        }
    }

    // Extract outputs (with type from definition attribute)
    const outputs: Array<{ $id?: string; name: string; type: string; truncate?: boolean }> = []
    let truncate = false
    for (const outputRecord of outputRecords) {
        const rawAttrName = outputRecord.get('resource_attribute_name')?.getValue() as string
        const attrInfo = resolveAttrInfo(rawAttrName)
        const truncateVal = outputRecord.get('truncate')?.getValue()
        const outputTruncate = truncateVal === true || truncateVal === 'true'
        if (attrInfo) {
            const output: { $id?: string; name: string; type: string; truncate?: boolean } = {
                $id: rawAttrName,
                name: attrInfo.name,
                type: attrInfo.dataType || 'object',
            }
            if (outputTruncate) {
                output.truncate = true
            }
            outputs.push(output)
        }
        // Track if any output has truncate for backward compatibility
        if (outputTruncate) {
            truncate = true
        }
    }

    const depends = extractDependsFromEdges(mappingId, edges, mappingIdToName, decisionMappingIds)
    const condition = extractConditionFromMapping(mapping)

    // Extract $capabilityId from the tool's capability record
    // Prefer explicit NowIdKey if available, otherwise fall back to the sys_id
    const resourceCapId = mapping.get('resource_capability')?.getValue() as string
    const capRecord = capabilityMap?.get(resourceCapId)
    const $capabilityId = capRecord
        ? ((capRecord.getId().getNowIdKey() as string | undefined) ?? resourceCapId)
        : resourceCapId

    // If only single 'Output' output, use backward-compatible truncate flag
    // Otherwise use dynamic outputs array
    const isSingleDefaultOutput = outputs.length === 1 && outputs[0]?.name === 'Output'

    return {
        type: TOOL_TYPES.SUBFLOW,
        name,
        $id: mappingId,
        $capabilityId,
        subflowId,
        inputs: inputs.length > 0 ? inputs : undefined,
        outputs: !isSingleDefaultOutput && outputs.length > 0 ? outputs : undefined,
        truncate: isSingleDefaultOutput ? truncate : undefined,
        depends,
        condition,
    }
}

/**
 * Build a FlowAction ToolDefinition from XML records.
 */
function buildFlowActionFromRecord(
    mapping: BuildRecord,
    attributeMappings: BuildRecord[],
    edges: BuildRecord[],
    mappingIdToName: Map<string, string>,
    decisionMappingIds: Set<string>,
    capDefMap?: Map<string, BuildRecord>,
    defAttrInfoMap?: Map<string, { name: string; dataType: string }>,
    capabilityMap?: Map<string, BuildRecord>
): ToolDefinition | null {
    const mappingId = mapping.getId().getValue() as string
    const name = mapping.get('resource_name')?.getValue() as string
    if (!name) {
        return null
    }

    const { inputs: inputRecords, outputs: outputRecords } = getAttributeMappingsForTool(mappingId, attributeMappings)

    // Extract actionId from metadata
    let actionId: string | undefined
    const metadataStr = mapping.get('metadata')?.getValue() as string
    if (metadataStr) {
        try {
            const metadata = JSON.parse(metadataStr)
            actionId = metadata.actionId
        } catch {}
    }

    // If actionId not in resource mapping metadata, try capability definition api field
    // (instance-sourced XML stores actionId as the capability definition's api value)
    if (!actionId && capDefMap) {
        const resourceCapId = mapping.get('resource_capability')?.getValue() as string
        const capDef = capDefMap.get(resourceCapId)
        if (capDef) {
            const api = capDef.get('api')?.getValue() as string
            if (api) {
                actionId = api
            }
        }
    }

    // Helper to resolve attribute name and type from defAttrInfoMap
    const resolveAttrInfo = (rawAttrName: string): { name: string; dataType: string } | null => {
        if (!rawAttrName) {
            return null
        }
        const info = defAttrInfoMap?.get(rawAttrName)
        if (info) {
            return info
        }
        // Fallback: use raw name as-is with default type
        return { name: rawAttrName, dataType: 'string' }
    }

    // Extract inputs (with type from definition attribute)
    const inputs: Array<{
        $id?: string
        name: string
        type: string
        value: string
        scriptValue?: string
        valueType?: 'template' | 'script'
    }> = []
    for (const inputRecord of inputRecords) {
        const rawAttrName = inputRecord.get('resource_attribute_name')?.getValue() as string
        const attrInfo = resolveAttrInfo(rawAttrName)
        const valueType = (inputRecord.get('attribute_value_type')?.getValue() as string) || 'template'
        if (attrInfo) {
            if (valueType === 'script') {
                const scriptValue = inputRecord.get('attribute_value_script')?.getValue() as string
                inputs.push({
                    $id: rawAttrName,
                    name: attrInfo.name,
                    type: attrInfo.dataType,
                    value: '',
                    scriptValue: scriptValue || '',
                    valueType: 'script',
                })
            } else {
                const template = inputRecord.get('attribute_value_template')?.getValue() as string
                inputs.push({ $id: rawAttrName, name: attrInfo.name, type: attrInfo.dataType, value: template || '' })
            }
        }
    }

    // Extract outputs (with type from definition attribute and truncate)
    const outputs: Array<{ $id?: string; name: string; type: string; truncate?: boolean }> = []
    for (const outputRecord of outputRecords) {
        const rawAttrName = outputRecord.get('resource_attribute_name')?.getValue() as string
        const attrInfo = resolveAttrInfo(rawAttrName)
        const truncateVal = outputRecord.get('truncate')?.getValue()
        const isTruncated = truncateVal === true || truncateVal === 'true'
        if (attrInfo) {
            outputs.push({
                $id: rawAttrName,
                name: attrInfo.name,
                type: attrInfo.dataType,
                ...(isTruncated ? { truncate: true } : {}),
            })
        }
    }

    const depends = extractDependsFromEdges(mappingId, edges, mappingIdToName, decisionMappingIds)

    // Extract $capabilityId from the tool's capability record
    // Prefer explicit NowIdKey if available, otherwise fall back to the sys_id
    const resourceCapId = mapping.get('resource_capability')?.getValue() as string
    const capRecord = capabilityMap?.get(resourceCapId)
    const $capabilityId = capRecord
        ? ((capRecord.getId().getNowIdKey() as string | undefined) ?? resourceCapId)
        : resourceCapId

    return {
        type: TOOL_TYPES.FLOW_ACTION,
        name,
        $id: mappingId,
        $capabilityId,
        actionId,
        inputs: inputs.length > 0 ? inputs : undefined,
        outputs: outputs.length > 0 ? outputs : undefined,
        depends,
    }
}

/**
 * Build a Skill ToolDefinition from XML records.
 */
function buildSkillFromRecord(
    mapping: BuildRecord,
    attributeMappings: BuildRecord[],
    edges: BuildRecord[],
    mappingIdToName: Map<string, string>,
    decisionMappingIds: Set<string>,
    defAttrNameMap?: Map<string, string>
): ToolDefinition | null {
    const mappingId = mapping.getId().getValue() as string
    const name = mapping.get('resource_name')?.getValue() as string
    if (!name) {
        return null
    }

    const { inputs: inputRecords, outputs: outputRecords } = getAttributeMappingsForTool(mappingId, attributeMappings)

    // Extract skillId from metadata or resource_capability
    let skillId: string | undefined
    let skillIdIsReference = false
    let skillIdExplicitId: string | undefined

    const metadataStr = mapping.get('metadata')?.getValue() as string
    if (metadataStr) {
        try {
            const metadata = JSON.parse(metadataStr)
            skillId = metadata.skillId
        } catch {}
    }

    // If skillId not in metadata, use resource_capability
    if (!skillId) {
        const resourceCapability = mapping.get('resource_capability')?.getValue() as string
        if (resourceCapability) {
            // Check if resource_capability is an explicitId reference (ends with __capability)
            // This indicates it was built with Now.ID['skill_name'] and should be transformed back
            if (resourceCapability.endsWith('__capability')) {
                // Extract the base skill ID by removing the __capability suffix
                const baseSkillId = resourceCapability.slice(0, -'__capability'.length)
                skillIdExplicitId = baseSkillId
                skillIdIsReference = true
                // skillId stays undefined - we'll use skillIdExplicitId for rendering
            } else {
                // It's a raw sys_id - use as-is
                skillId = resourceCapability
            }
        }
    }

    // Extract inputs — use raw resource_attribute_name (sys_id of definition attribute)
    // directly as the name field. buildSkillInputShapes emits it as definitionAttributeId.
    const inputs: Array<{
        name: string
        value: string
        scriptValue?: string
        valueType?: 'template' | 'script'
    }> = []
    for (const inputRecord of inputRecords) {
        const rawAttrName = inputRecord.get('resource_attribute_name')?.getValue() as string
        if (!rawAttrName) {
            continue
        }

        const valueType = (inputRecord.get('attribute_value_type')?.getValue() as string) || 'template'
        if (valueType === 'script') {
            const scriptValue = inputRecord.get('attribute_value_script')?.getValue() as string
            inputs.push({
                name: rawAttrName,
                value: '',
                scriptValue: scriptValue || '',
                valueType: 'script',
            })
        } else {
            const template = inputRecord.get('attribute_value_template')?.getValue() as string
            inputs.push({ name: rawAttrName, value: template || '' })
        }
    }

    // Extract unified skillOutputs — all outputs (fixed + custom) keyed by resolved name
    // resource_attribute_name stores the sys_id of the target skill's definition attribute
    const skillOutputs: Record<string, { definitionAttributeId: string | Shape; truncate?: boolean }> = {}
    for (const outputRecord of outputRecords) {
        const rawAttrName = outputRecord.get('resource_attribute_name')?.getValue() as string
        if (!rawAttrName) {
            continue
        }
        const resolvedName = resolveAttributeName(rawAttrName, defAttrNameMap)
        // Skip legacy 'Output'
        if (resolvedName === 'Output') {
            continue
        }
        // Use resolved name as key if available, otherwise fall back to raw sys_id
        // Use resolved name as key, normalizing via OUTPUT_NAME_TO_TRUNCATE_KEY mapping
        const rawKey = resolvedName || rawAttrName
        const attrName =
            SKILL_TOOL_CONFIG.OUTPUT_NAME_TO_TRUNCATE_KEY[
                rawKey as keyof typeof SKILL_TOOL_CONFIG.OUTPUT_NAME_TO_TRUNCATE_KEY
            ] || rawKey

        const truncateVal = outputRecord.get('truncate')?.getValue()
        const isTruncated = truncateVal === true || truncateVal === 'true'

        skillOutputs[attrName] = {
            definitionAttributeId: rawAttrName,
            ...(isTruncated ? { truncate: true } : {}),
        }
    }

    const depends = extractDependsFromEdges(mappingId, edges, mappingIdToName, decisionMappingIds)

    return {
        type: TOOL_TYPES.SKILL,
        name,
        $id: mappingId,
        skillId,
        skillIdIsReference,
        skillIdExplicitId,
        inputs: inputs.length > 0 ? inputs : undefined,
        skillOutputs: Object.keys(skillOutputs).length > 0 ? skillOutputs : undefined,
        depends,
    }
}

/**
 * Build a Decision ToolDefinition from XML records.
 */
function buildDecisionFromRecord(
    mapping: BuildRecord,
    _attributeMappings: BuildRecord[],
    edges: BuildRecord[],
    mappingIdToName: Map<string, string>,
    parentMappingIds: Set<string>,
    decisionMappingIds: Set<string>
): ToolDefinition | null {
    const mappingId = mapping.getId().getValue() as string
    const name = mapping.get('resource_name')?.getValue() as string
    if (!name) {
        return null
    }

    // Decision targets are the tools this decision can route to, plus '_end' if it routes to the skill prompt.
    // They appear as to_resource in edges where from_resource = this decision's ID
    const targetsSet = new Set<string>()
    const targets: string[] = []
    const branches: Array<{ name: string; target: string; condition: string; conditionType: 'filter' | 'script' }> = []
    let defaultTarget: string | undefined

    for (const edge of edges) {
        const fromId = edge.get('from_resource')?.getValue() as string
        if (fromId !== mappingId) {
            continue
        }
        const toId = edge.get('to_resource')?.getValue() as string

        // Check if edge points to parent skill mapping (prompt)
        const isEdgeToPrompt = parentMappingIds.has(toId)

        // For prompt edges use '_end', otherwise get the target tool name
        const targetName = isEdgeToPrompt ? '_end' : mappingIdToName.get(toId)
        if (!targetName) {
            continue
        }

        // Add to targets array (including '_end' for prompt-pointing edges), deduplicating
        if (!targetsSet.has(targetName)) {
            targetsSet.add(targetName)
            targets.push(targetName)
        }

        // Extract branch name, condition, and order from edge
        const edgeName = (edge.get('name')?.getValue() as string) || ''
        const applicabilityConditionExpr = edge.get('applicability_condition_expression')?.getValue() as string
        const applicabilityScript = edge.get('applicability_script')?.getValue() as string
        const edgeOrder = edge.get('order')?.getValue() as number | string

        // Default branch detection:
        // 1. Edge name is 'Default branch', OR
        // 2. Edge order is 1000 (platform convention for default), OR
        // 3. No condition expression AND (no script OR script is always-true)
        const isDefaultBranch =
            edgeName === 'Default branch' ||
            edgeOrder === 1000 ||
            edgeOrder === '1000' ||
            (!applicabilityConditionExpr && (!applicabilityScript || applicabilityScript.includes('return true;')))

        if (isDefaultBranch) {
            // Default can point to a tool or to 'prompt' (parent skill)
            // When pointing to prompt, use '_end' as the target name
            defaultTarget = isEdgeToPrompt ? '_end' : targetName
        } else {
            // Conditional edge — target is either a tool name or '_end' (prompt)
            // Read applicability_type to determine which field to use
            const applicabilityType = edge.get('applicability_type')?.getValue() as string
            const isScript = applicabilityType === 'script'
            const condition = isScript ? applicabilityScript || '' : applicabilityConditionExpr || ''
            const conditionType = isScript ? 'script' : 'filter'
            branches.push({ name: edgeName, target: targetName, condition, conditionType })
        }
    }

    const depends = extractDependsFromEdges(mappingId, edges, mappingIdToName, decisionMappingIds)

    return {
        type: TOOL_TYPES.DECISION,
        name,
        $id: mappingId,
        targets: targets.length > 0 ? targets : undefined,
        branches: branches.length > 0 ? branches : undefined,
        default: defaultTarget,
        depends,
    }
}
